import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconShieldLock } from '@tabler/icons-react';
import PasswordField from '@/components/auth/PasswordField';
import { userManagementService } from '@/api/services/UserManagementService';
import { getApiMessage, getStatus, isNetworkError } from '@/api/errors';
import { isBlank } from '@/lib/validators';
import type { AuthTokens } from '@/types/auth';

interface PasswordValues {
  currentPassword: string;
  newPassword: string;
  repeatPassword: string;
}

const MIN_LENGTH = 8;

interface Props {
  readonly submitLabel: string;
  /**
   * Вызывается после успешной смены. `tokens` — свежая пара (бэкенд переполучил после отзыва
   * старых Identix'ом) для бесшовной подмены, либо `null` — тогда вызывающий должен разлогинить.
   */
  readonly onSuccess: (tokens: AuthTokens | null) => void;
}

/**
 * Форма смены собственного пароля (текущий → новый → повтор). Текущий пароль проверяет Identix;
 * серверные ошибки раскладываем на поля: 422 — «неверный текущий», 400 — «новый отклонён».
 * Используется и во вкладке «Безопасность», и в форс-смене (временный пароль).
 */
export default function ChangePasswordForm({ submitLabel, onSuccess }: Props) {
  const { t } = useTranslation();

  const form = useForm<PasswordValues>({
    mode: 'uncontrolled',
    initialValues: { currentPassword: '', newPassword: '', repeatPassword: '' },
    validate: {
      currentPassword: (value) => (isBlank(value) ? t('auth.validation.required') : null),
      newPassword: (value, values) => {
        if (isBlank(value)) {
          return t('auth.validation.required');
        }
        if (value.length < MIN_LENGTH) {
          return t('app.profile.security.tooShort');
        }
        if (value === values.currentPassword) {
          return t('app.profile.security.sameAsCurrent');
        }
        return null;
      },
      repeatPassword: (value, values) =>
        value !== values.newPassword ? t('app.profile.security.mismatch') : null,
    },
    validateInputOnBlur: true,
  });

  const mutation = useMutation({
    mutationFn: (values: PasswordValues) =>
      userManagementService.changeOwnPassword(values.currentPassword, values.newPassword),
    onSuccess: (tokens) => {
      form.reset();
      onSuccess(tokens);
    },
    onError: (error) => {
      const status = getStatus(error);
      if (status === 422) {
        form.setFieldError('currentPassword', t('app.profile.security.currentInvalid'));
      } else if (status === 400) {
        form.setFieldError('newPassword', t('app.profile.security.newRejected'));
      } else {
        notifications.show({
          color: 'red',
          message: isNetworkError(error)
            ? t('auth.errors.generic')
            : (getApiMessage(error) ?? t('auth.errors.generic')),
        });
      }
    },
  });

  return (
    <form onSubmit={form.onSubmit((values) => mutation.mutate(values))} noValidate>
      <Stack gap="sm" maw={360}>
        <PasswordField
          label={t('app.profile.security.current')}
          autoComplete="current-password"
          withAsterisk
          key={form.key('currentPassword')}
          {...form.getInputProps('currentPassword')}
        />
        <PasswordField
          label={t('app.profile.security.new')}
          autoComplete="new-password"
          withAsterisk
          key={form.key('newPassword')}
          {...form.getInputProps('newPassword')}
        />
        <PasswordField
          label={t('app.profile.security.repeat')}
          autoComplete="new-password"
          withAsterisk
          key={form.key('repeatPassword')}
          {...form.getInputProps('repeatPassword')}
        />
        <Button
          type="submit"
          leftSection={<IconShieldLock size={18} />}
          w="fit-content"
          loading={mutation.isPending}
        >
          {submitLabel}
        </Button>
      </Stack>
    </form>
  );
}
